import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import type { ExperienceSession, PlaybackGate, VoiceState, VoiceTurn } from '@aeonia/gaia-client';
import { bindPageHidden } from '@aeonia/gaia-client';
import { createVoiceTurn } from './session';

export interface UseVoiceTurn { voice: VoiceState; press: () => void; enableSpeech: () => void }

/** One VoiceTurn for the life of the page, disposed with the same deferral useSession uses. */
export default function useVoiceTurn(session: ExperienceSession, playbackGate?: PlaybackGate): UseVoiceTurn {
  const ref = useRef<VoiceTurn>(null);
  if (ref.current === null) {
    ref.current = createVoiceTurn(session);
    if (playbackGate) ref.current.setPlaybackGate(playbackGate);
  }
  const turn = ref.current;

  const subscribe = useCallback((onChange: () => void) => turn.subscribe(() => onChange()), [turn]);
  const voice = useSyncExternalStore(subscribe, () => turn.state, () => turn.state);

  useEffect(() => bindPageHidden(turn), [turn]);

  const disposeTimer = useRef(0);
  useEffect(() => {
    clearTimeout(disposeTimer.current);
    return () => { disposeTimer.current = window.setTimeout(() => turn.dispose(), 0); };
  }, [turn]);

  const press = useCallback(() => turn.press(), [turn]);
  const enableSpeech = useCallback(() => turn.enableSpeech(), [turn]);
  return { voice, press, enableSpeech };
}
